import {
  createApi,
  fetchBaseQuery,
  FetchBaseQueryError,
} from "@reduxjs/toolkit/dist/query/react";
import moment from "moment/moment";

import * as devConfig from "../app/configs/devConfig";
import { getAuthHeader } from "../app/globalState/GlobalAuthState";
import { NotificationsCountsModel } from "../factories/notifications/models/notifications-counts.model";
import { serverErrorMessage } from "../features/settings/workTypesV2/model/workTypeEditor.utils";
import { invoiceableTimes, InvoiceTime } from "../features/time/InvoiceTime";
import {
  INotification,
  IPermissions,
  ITime,
  IUser,
  IUserRoleEnum,
} from "../types/types";
import { TeamInvitation } from "../features/settings/team/invites/teamInvites.utils";
import { contactsSlice } from "./ContactsSlice";
import { workNotesSlice } from "./WorkNotesSlice";

export type FilterTimeResponse = {
  pageNumber: number;
  pageSize: number;
  totalCount: number;
  totalPages: number;
  items: ITime[];
};

export const userSlice = createApi({
  reducerPath: "userSlice",
  tagTypes: ["Notifications", "Time", "Users", "UserRoles", "Invitations"],
  baseQuery: fetchBaseQuery({
    baseUrl: `${devConfig.getServerBaseUrlForEnv()}`,
    prepareHeaders: (headers) => {
      const authHeader = getAuthHeader();
      headers.set("Authorization", authHeader.Authorization);
      return headers;
    },
  }),
  endpoints: (build) => ({
    getUser: build.query<IUser, { userId: string; orgId: string }>({
      query: ({ userId, orgId }) => ({
        url: `api/users/${userId}`,
        params: {
          orgId: orgId,
        },
      }),
      providesTags: ["Users"],
    }),
    getUsers: build.query<IUser[], string>({
      query: (orgId) => ({
        url: "api/users/search",
        params: {
          orgId: orgId,
        },
      }),
      providesTags: ["Users"],
    }),
    getUserRoleEnum: build.query<IUserRoleEnum[], void>({
      query: () => ({
        url: "api/user-role-enum/get-all",
      }),
      providesTags: ["UserRoles"],
    }),
    getArchivedUsers: build.query<IUser[], string>({
      query: (orgId) => ({
        url: "api/users/get-archive-users",
        params: {
          orgId: orgId,
        },
      }),
      providesTags: ["Users"],
    }),
    archiveUser: build.mutation<unknown, IUser>({
      query: (user) => ({
        url: "api/users/archive-user",
        method: "PUT",
        body: user,
      }),
      invalidatesTags: ["Users"],
    }),
    unarchiveUser: build.mutation<unknown, IUser>({
      query: (user) => ({
        url: "api/users/unarchive-user",
        method: "PUT",
        body: user,
      }),
      invalidatesTags: ["Users"],
    }),
    // Settings > Team > Invitations (2026-10-25): status, Resend, Cancel.
    getTeamInvitations: build.query<TeamInvitation[], string>({
      query: (orgId) => ({
        url: `api/user-invitations/list?orgId=${encodeURIComponent(orgId)}`,
      }),
      providesTags: ["Invitations"],
    }),
    resendInvitation: build.mutation<TeamInvitation, { id: string; orgId: string }>({
      query: ({ id, orgId }) => ({
        url: `api/user-invitations/${id}/resend?orgId=${encodeURIComponent(orgId)}`,
        method: "POST",
      }),
      invalidatesTags: ["Invitations"],
      transformErrorResponse: (res: FetchBaseQueryError): string =>
        serverErrorMessage(res, "The invitation couldn't be sent. Please try again."),
    }),
    cancelInvitation: build.mutation<unknown, { id: string }>({
      query: ({ id }) => ({
        url: "api/user-invitations",
        method: "DELETE",
        body: { id },
      }),
      invalidatesTags: ["Invitations"],
    }),
    addUser: build.mutation<
      unknown,
      {
        orgId: string;
        userRoleEnumId: string;
        firstName: string;
        lastName: string;
        email: string;
        permissionIds: string[];
        assignedCustomerIds: string[];
        assignedAllClients: boolean;
        /** Free seasonal user (Feb 1 – Apr 30), firms billed per user only. */
        seasonal?: boolean;
      }
    >({
      query: (user) => ({
        url: "api/user-invitations/add-user-invitation",
        method: "POST",
        body: user,
      }),
      invalidatesTags: ["Users", "Invitations"],
      // Always a sentence to show: the server's message for a 400 (email or
      // name in use), otherwise a plain "couldn't send" (before, a failed
      // request showed nothing and a 500 showed technical text).
      transformErrorResponse: (res: FetchBaseQueryError): string =>
        serverErrorMessage(
          res,
          "The invitation couldn't be sent. Please check your connection and try again.",
        ),
    }),
    editUser: build.mutation<unknown, IUser>({
      query: (user) => ({
        url: "api/users/update-user",
        method: "PUT",
        body: user,
      }),
      onQueryStarted: (_, api) => {
        api.queryFulfilled.then(() => {
          api.dispatch(contactsSlice.util?.invalidateTags(["Contacts"]));
        });
      },
      invalidatesTags: ["Users"],
    }),
    getPermissions: build.query<IPermissions[], void>({
      query: () => ({
        url: "api/permissions/get-list",
      }),
      providesTags: ["Users"],
    }),
    deleteUser: build.mutation<unknown, IUser>({
      query: (user) => ({
        url: "api/users",
        method: "DELETE",
        body: user,
      }),
      invalidatesTags: ["Users"],
    }),

    getCountsNotifications: build.query<
      NotificationsCountsModel,
      { userId: string; orgId: string; isRead: boolean }
    >({
      query: (params) => ({
        url: "/api/notifications/get-counts",
        params,
      }),
      providesTags: ["Notifications"],
    }),
    getUnreadNotifications: build.query<
      INotification[],
      { userId: string; orgId: string; notificationType: string }
    >({
      query: ({ userId, orgId, notificationType }) => ({
        url: "/api/notifications/get-unread-notifications",
        params: {
          userId,
          orgId,
          notificationType,
        },
      }),
      providesTags: ["Notifications"],
    }),
    getReadNotifications: build.query<
      INotification[],
      { userId: string; orgId: string; notificationType: string }
    >({
      query: ({ userId, orgId, notificationType }) => ({
        url: "/api/notifications/get-read-notifications",
        params: {
          userId,
          orgId,
          notificationType,
        },
      }),
      providesTags: ["Notifications"],
    }),
    markAsReadNotification: build.mutation({
      query: (notificationIds: string[]) => ({
        url: "/api/notifications/mark-as-read",
        method: "PUT",
        body: {
          notificationIds,
        },
      }),
      invalidatesTags: ["Notifications"],
    }),
    markAsUnreadNotification: build.mutation({
      query: (notificationIds: string[]) => ({
        url: "/api/notifications/mark-as-unread",
        method: "PUT",
        body: {
          notificationIds,
        },
      }),
      invalidatesTags: ["Notifications"],
    }),
    deleteNotification: build.mutation({
      query: (notificationIds: string[]) => ({
        url: "/api/notifications/delete-notification",
        method: "DELETE",
        body: {
          notificationIds,
        },
      }),
      invalidatesTags: ["Notifications"],
    }),
    mentionNotification: build.mutation({
      query: ({
        userIds,
        userId,
        workId,
        customerId,
      }: {
        userIds: string[];
        userId: string;
        workId?: string;
        customerId?: string;
      }) => ({
        url: "/api/notifications/mention-user",
        method: "POST",
        params: {
          mentionByUser: userId,
          jobId: workId,
          customerId: customerId,
        },
        body: {
          notificationIds: userIds,
        },
      }),
      onQueryStarted: (_, api) => {
        api.queryFulfilled.then(() => {
          api.dispatch(workNotesSlice.util?.invalidateTags(["Participants"]));
        });
      },
    }),
    receiveNotifications: build.mutation<
      unknown,
      { userId: string; receiveEmail: boolean }
    >({
      query: ({ userId, receiveEmail }) => ({
        url: "api/users/receive-notification-emails",
        method: "PUT",
        params: { userId, receiveEmail },
      }),
      invalidatesTags: ["Users"],
    }),
    syncEmails: build.mutation<unknown, { userId: string; syncEmail: boolean }>(
      {
        query: ({ userId, syncEmail }) => ({
          url: "api/users/sync-contact-emails",
          method: "PUT",
          params: { userId, syncEmail },
        }),
        invalidatesTags: ["Users"],
      },
    ),
    saveTimer: build.mutation({
      query: (body) => ({
        url: "api/time/upload",
        method: "POST",
        body: { ...body, date: moment().utc() },
      }),
      invalidatesTags: ["Time"],
    }),
    updateEmail: build.mutation<unknown, { userId: string; email: string }>({
      query: ({ userId, email }) => ({
        url: "api/users/update-email-for-notification",
        method: "PUT",
        params: { userId, email },
      }),
      invalidatesTags: ["Users"],
    }),
    getBilledHours: build.query<
      {
        billedAmount: number;
        billedHours: number;
        unBilledHours: number;
      },
      { orgId: string }
    >({
      query: ({ orgId }) => ({
        url: "/api/time/get-billed-hours",
        params: {
          orgId,
        },
      }),
      providesTags: ["Time"],
    }),
    getBilledHoursByCustomerId: build.query<
      {
        billedAmount: number;
        billedHours: number;
        unBilledHours: number;
      },
      { orgId: string; customerId: string }
    >({
      query: ({ orgId, customerId }) => ({
        url: "/api/time/get-billed-hours-by-CustomerId",
        params: {
          orgId,
          customerId,
        },
      }),
      providesTags: ["Time"],
    }),
    getBilledHoursByWorkId: build.query<
      {
        billedAmount: number;
        billedHours: number;
        unBilledHours: number;
      },
      { orgId: string; workId: string }
    >({
      query: ({ orgId, workId }) => ({
        url: "/api/time/get-billed-hours-by-WorkId",
        params: {
          orgId,
          workId,
        },
      }),
      providesTags: ["Time"],
    }),
    getWorksByCustomerId: build.mutation<InvoiceTime[], { customerId: string }>(
      {
        query: ({ customerId }) => ({
          url: `/api/time/get-by-customerId/${customerId}`,
        }),
        // Only billable time that isn't on an invoice yet is offered (QA C1:
        // billed time came back and could be billed twice).
        transformResponse: (times: InvoiceTime[]) => invoiceableTimes(times),
        invalidatesTags: ["Users"],
      },
    ),
    addBilledWork: build.mutation({
      query: (body) => ({
        url: "api/invoice-item/add-billed-item",
        method: "POST",
        body,
      }),
    }),
    updateBilledWork: build.mutation<unknown, unknown>({
      query: (body) => ({
        url: "api/invoice-item/update-billed-item",
        method: "PUT",
        body,
      }),
    }),
    filterTime: build.query<
      FilterTimeResponse,
      {
        filterByDate?: string[];
        filterByWorkType?: string[];
        filterByClient?: string[];
        filterByAssignee?: string[];
        filterByStatus?: boolean | null;
        filterByBillable?: boolean | null;
        fromDate?: string | null;
        toDate?: string | null;
        orgId?: string;
        sortOrder: string;
        sortBy: string;
        filterByWork?: string[];
        filterByWorkTask?: string[];
        pageNumber?: number;
        pageSize?: number;
      }
    >({
      query: (body) => ({
        url: "api/time/filter-time",
        method: "POST",
        body,
      }),
      providesTags: ["Time"],
    }),
  }),
});
export const {
  //Users
  useGetUserQuery,
  useGetUsersQuery,
  useGetUserRoleEnumQuery,
  useGetArchivedUsersQuery,
  useAddUserMutation,
  useGetTeamInvitationsQuery,
  useResendInvitationMutation,
  useCancelInvitationMutation,
  useEditUserMutation,
  useGetPermissionsQuery,
  useDeleteUserMutation,
  useArchiveUserMutation,
  useUnarchiveUserMutation,

  //Notifications
  useGetCountsNotificationsQuery,
  useGetUnreadNotificationsQuery,
  useGetReadNotificationsQuery,
  useMarkAsReadNotificationMutation,
  useMarkAsUnreadNotificationMutation,
  useDeleteNotificationMutation,
  useMentionNotificationMutation,
  useReceiveNotificationsMutation,
  useUpdateEmailMutation,

  useSyncEmailsMutation,
  useSaveTimerMutation,

  //Billable hours
  useGetBilledHoursQuery,
  useGetBilledHoursByCustomerIdQuery,
  useGetBilledHoursByWorkIdQuery,
  useGetWorksByCustomerIdMutation,
  useAddBilledWorkMutation,
  useUpdateBilledWorkMutation,
  useFilterTimeQuery,
  useLazyFilterTimeQuery,
} = userSlice;
